<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import Button from 'primevue/button'

const posCheckStore = usePosCheckStore()
const { t } = useNuxtApp().$i18n

const { checkCart, itemsInCart, checkCartTotal, hasActiveCheck, compactViewEnabled, checkData, isEditingClosedCheck } = storeToRefs(posCheckStore)
const { closeCheckWithPayment, closeCheck, saveClosedCheckEdits, cancelClosedCheckEdit } = posCheckStore

function handleCancelEdit() {
  cancelClosedCheckEdit()
  navigateTo('/app/checks')
}

const showCloseCheckDialog = ref(false)
const { $confirm } = useNuxtApp()

function handleCloseCheck() {
  showCloseCheckDialog.value = true
}

async function handleCloseCheckConfirm(paymentData: {
  tax: number
  discount_amount: number
  tip: number
  payment_method_id: number | null
  payment_method_name: string | null
  discount_id: number | null
  discount_percentage: number | null
  tenders: import('~/types/checks').CheckTender[]
}) {
  if (isEditingClosedCheck.value) {
    const ok = await saveClosedCheckEdits(paymentData)
    if (ok) showCloseCheckDialog.value = false
  }
  else {
    const success = await closeCheckWithPayment(paymentData)
    if (success) {
      showCloseCheckDialog.value = false
    }
  }
}

// Handle cancel check (only when cart is empty)
function handleCancelCheck() {
  $confirm.require({
    group: 'inline',
    message: t('Cancel this empty check? It can be reopened later.'),
    icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: t('No'),
      severity: 'secondary',
      outlined: true,
    },
    acceptProps: {
      label: t('Yes, Cancel'),
      severity: 'danger',
    },
    accept: async () => {
      await closeCheck()
    },
  })
}
</script>

<template>
  <div
    v-if="hasActiveCheck"
    class="check-cart bg-white shadow-xl rounded-lg flex flex-col h-full border border-gray-200"
  >
    <!-- Customer + Compact View Toggle -->
    <div class="p-2 border-b border-gray-200 flex items-center justify-between">
      <div
        v-if="checkData?.customer_name || checkData?.custom_label"
        class="flex items-center gap-2 text-sm text-gray-700"
      >
        <i :class="['text-xs', checkData?.customer_name ? 'pi pi-user' : 'pi pi-tag']" />
        <span class="font-medium">{{ checkData.customer_name || checkData.custom_label }}</span>
      </div>
      <div v-else />
      <Button
        :icon="compactViewEnabled ? 'pi pi-list' : 'pi pi-align-justify'"
        :label="compactViewEnabled ? t('Expanded') : t('Compact')"
        severity="secondary"
        text
        size="small"
        @click="compactViewEnabled = !compactViewEnabled"
      />
    </div>

    <!-- Active Check Header -->
    <!-- <div
      :class="[
        'bg-gradient-to-r from-green-600 to-green-700 text-white p-5 rounded-t-lg transition-all duration-300',
        isFlashing ? 'ring-4 ring-yellow-400 ring-offset-2 scale-[1.02]' : '',
      ]"
    >
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-3">
          <div class="bg-white/20 backdrop-blur-sm rounded-lg p-2">
            <i class="pi pi-table text-2xl" />
          </div>
          <div>
            <div class="text-xl font-bold tracking-wide">
              {{ selectedTable ? selectedTable.table_number : t('Check') }}
            </div>
            <div class="text-sm font-medium opacity-90">
              #{{ checkData ? getCheckSequence(checkData.check_number) : '' }}
            </div>
          </div>
        </div>
        <Button
          :label="t('Park')"
          icon="pi pi-pause-circle"
          class="!bg-white hover:!bg-cyan-600 !border-2 !border-cyan-600 !text-cyan-700 hover:!text-white transition-colors"
          size="small"
          @click="handleParkCheck"
        />
      </div>
    </div> -->

    <!-- Cart Items -->
    <div class="flex-1 overflow-y-auto p-0 bg-gray-50/50">
      <POSCheckCartReviewDetails
        :cart="checkCart"
        :show-actions="true"
        :compact-view="compactViewEnabled"
      />
    </div>

    <!-- Total Section -->
    <div class="p-1 border-t-2 border-gray-200 bg-white">
      <div class="flex justify-between items-center mb-2 bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-lg border border-gray-200">
        <span class="text-lg font-semibold text-gray-700">{{ t('Total') }}:</span>
        <span class="text-2xl font-bold text-emerald-600">{{ usePriceFormatter(Number(checkCartTotal)) }}</span>
      </div>

      <!-- Closed Check Edit Mode Actions -->
      <template v-if="isEditingClosedCheck">
        <div class="flex gap-2 mb-2">
          <Button
            :label="t('Back')"
            icon="pi pi-arrow-left"
            severity="secondary"
            outlined
            class="flex-1 font-semibold py-3"
            @click="handleCancelEdit"
          />
          <Button
            :label="t('Close Check')"
            icon="pi pi-check-circle"
            severity="info"
            class="flex-1 font-semibold py-3"
            @click="handleCloseCheck"
          />
        </div>
      </template>

      <!-- Normal Mode Action Buttons -->
      <template v-else>
        <Button
          v-if="itemsInCart > 0"
          :label="t('Close Check')"
          icon="pi pi-check-circle"
          severity="info"
          class="w-full text-base font-semibold py-3"
          @click="handleCloseCheck"
        />
        <Button
          v-else
          :label="t('Cancel Check')"
          icon="pi pi-times-circle"
          severity="danger"
          outlined
          class="w-full text-base font-semibold py-3"
          @click="handleCancelCheck"
        />
      </template>
    </div>

    <!-- Close Check Dialog -->
    <POSCloseCheckDialog
      v-if="showCloseCheckDialog"
      v-model:visible="showCloseCheckDialog"
      @confirm="handleCloseCheckConfirm"
    />
  </div>
</template>
